<template>
  <div>
    <gc-alert v-if="submitError" type="error" text>
      {{ $t('message.error.body') }}
    </gc-alert>

    <gc-dialog v-model="dealBreakerShown" max-width="600" persistent>
      <v-card>
        <v-card-title v-if="dealBreakerData.title" class="text-h6">
          {{ dealBreakerData.title }}
        </v-card-title>

        <v-card-text :class="{ 'pt-5': !dealBreakerData.title }">
          <slot>
            <template v-if="typeof dealBreakerData.text === 'string'">
              <strong>{{ dealBreakerData.text }}</strong>
            </template>
            <template v-else>
              <div v-for="(text, num) in dealBreakerData.text" v-bind:key="num">
                <strong v-if="num === 0">
                  {{ text }}
                </strong>
                <span v-else>
                  {{ text }}
                </span>
                <gc-divider
                  v-if="num < dealBreakerData.text.length - 1"
                  class="gc-my-3"
                />
              </div>
            </template>
            <div class="gc-mt-2" v-if="!dealBreakerHideContact">
              {{ $t('dealBreaker.contact.text') }}
              <a class="gc-text-primary" @click="onClickGoToContact">
                {{ $t('dealBreaker.contact.link') }}
              </a>
            </div>
          </slot>
          <slot name="additionalContent"></slot>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dealBreakerShown = false">
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </gc-dialog>

    <div class="gc-mt-2 gc-flex gc-justify-between">
      <gc-button
        v-if="showBackButton"
        data-testid="back-button"
        large
        :disabled="globalState.submitting"
        class="gc-mr-3"
        :class="showNextButton ? 'gc-flex-grow' : 'gc-w-1/2'"
        @click="prev()"
      >
        <font-awesome-icon :icon="faAngleLeft" class="gc-mr-2" />
        {{ $t('button.navigation.back') }}
      </gc-button>

      <gc-button
        v-if="showNextButton"
        color="primary"
        data-testid="next-button"
        large
        class="submit-button gc-flex-grow"
        :loading="globalState.submitting"
        :disabled="!valid || globalState.submitting"
        @click="handleNext()"
      >
        <span>
          {{ isSubmit ? $t('submit') : $t('button.navigation.next') }}</span
        >
        <font-awesome-icon
          v-if="!isSubmit"
          :icon="faAngleRight"
          class="gc-ml-2"
        />
      </gc-button>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType, computed, defineComponent, ref } from '@vue/composition-api';
import { faAngleLeft, faAngleRight } from '@fortawesome/pro-solid-svg-icons';
import GcButton from '@/components/primitives/GcButton.vue';
import GcAlert from '@/components/primitives/GcAlert.vue';
import GcDialog from '@/components/primitives/GcDialog.vue';
import GcDivider from '@/components/primitives/GcDivider.vue';
import i18n from '@/plugins/i18n';
import {
  DealBreakerData,
  StepDefinition,
  stepSubmitFunctionType,
} from '@/interfaces/step-definition';
import { AssistantContext } from '~/steps/steps';
import { useState } from '@/composables/globalState';

export default defineComponent({
  components: {
    GcAlert,
    GcButton,
    GcDialog,
    GcDivider,
  },
  props: {
    assistantContext: {
      default: null,
      type: Object as PropType<AssistantContext>,
    },
    showBackButton: {
      default: true,
      type: Boolean,
    },
    showNextButton: {
      default: true,
      type: Boolean,
    },
    step: {
      required: true,
      type: Object as PropType<StepDefinition>,
    },
    steps: {
      default: null,
      type: Array as PropType<StepDefinition[]>,
    },
    submit: {
      default: null,
      type: Function as PropType<stepSubmitFunctionType>,
    },
    valid: {
      default: true,
      type: Boolean,
    },
  },
  setup: (props, { emit }) => {
    const next = () => {
      emit('next');
    };

    const prev = () => {
      emit('prev');
    };

    const submitError = ref<boolean | string>(false);

    const isSubmit = computed<boolean>(() => !!props.submit);

    const { globalState, setSubmitting } = useState();

    const doSubmit = async () => {
      if (typeof props.submit !== 'function') {
        return;
      }
      submitError.value = false;
      setSubmitting(true);
      props
        .submit({
          assistantContext: props.assistantContext,
          i18n,
          steps: props.steps as StepDefinition[],
        })
        .then(() => {
          emit('submit_success');
          next();
        })
        .catch((error) => {
          emit('submit_error');
          submitError.value = error;
        })
        .finally(() => {
          setSubmitting(false);
        });
    };

    const dealBreakerShown = ref(false);
    const dealBreakerData = ref<DealBreakerData | false>(false);

    const isDealBreaker = (): boolean => {
      if (!props.step?.isDealBreaker) {
        return false;
      }

      dealBreakerData.value = props.step?.isDealBreaker(
        props.assistantContext as AssistantContext,
        i18n,
      );
      if (dealBreakerData.value === false) {
        return false;
      }

      dealBreakerShown.value = true;

      return true;
    };

    const dealBreakerHideContact = computed(
      () =>
        isDealBreaker() &&
        props.step?.hideSkipMessage &&
        props.step?.hideSkipMessage(props.assistantContext as AssistantContext),
    );

    const onClickGoToContact = () => {
      emit('transition', {
        event: 'JUMP_TO_CONTACTFORM',
      });
    };

    // const goToStep = (stepId: StepId) => {
    //   emit('transition', {
    //     event: 'JUMP_TO_' + stepId.toUpperCase(),
    //   });
    // };

    const handleNext = () => {
      if (isDealBreaker()) {
        return;
      }

      if (isSubmit.value) {
        doSubmit();
        return;
      }

      next();
    };

    return {
      dealBreakerData,
      dealBreakerShown,
      faAngleLeft,
      faAngleRight,
      handleNext,
      isSubmit,
      prev,
      submitError,
      globalState,
      dealBreakerHideContact,
      onClickGoToContact,
      // goToStep,
    };
  },
});
</script>
